<template>
  <div class="flex w-full flex-col gap-6">
    <Button class="w-fit" as-child variant="outline">
      <RouterLink :to="store.selected?.company ? { name: 'CompanySupportRequestsPage', params: { companyUuid: store.selected.company.uuid } } : { name: 'SupportRequestsPage' }">
        {{ store.selected?.company ? `Support requests for ${store.selected.company.name}` : 'All support requests' }}
      </RouterLink>
    </Button>
    <Alert v-if="store.error" variant="destructive" role="alert">
      <AlertTitle>Support request unavailable</AlertTitle>
      <AlertDescription>{{ store.error }}</AlertDescription>
    </Alert>
    <p v-if="store.isLoading" role="status" class="text-sm text-muted-foreground">
      Loading support request…
    </p>
    <template v-else-if="store.selected">
      <header class="flex flex-col gap-3 border-b pb-6">
        <p class="text-sm text-muted-foreground">
          Request #{{ store.selected.id }} · {{ store.selected.company?.name ?? 'No company' }}
        </p>
        <h1 class="text-3xl font-semibold tracking-tight">
          {{ store.selected.subject }}
        </h1>
        <p class="text-sm text-muted-foreground">
          {{ store.selected.category_label }} · Opened by {{ store.selected.user.name }} on {{ formatDate(store.selected.created_at) }}
        </p>
        <p v-if="store.selected.closed_at" class="text-sm text-muted-foreground">
          Closed {{ formatDate(store.selected.closed_at) }}
        </p>
      </header>
      <div class="flex flex-wrap items-center gap-3">
        <label for="request-status" class="text-sm font-medium">Status</label>
        <select id="request-status" :value="store.selected.status" class="h-10 rounded-md border bg-background px-3 text-sm" :disabled="store.isSaving" @change="changeStatus">
          <option v-if="!store.selected.available_statuses.includes(store.selected.status)" :value="store.selected.status" disabled>
            {{ statusLabel(store.selected.status) }}
          </option>
          <option v-for="status in store.selected.available_statuses" :key="status" :value="status">
            {{ statusLabel(status) }}
          </option>
        </select>
      </div>
      <section class="flex flex-col gap-3 rounded-xl border bg-card p-5">
        <h2 class="font-semibold">
          Description
        </h2>
        <p class="whitespace-pre-wrap text-sm leading-6">
          {{ store.selected.description }}
        </p>
      </section>
      <NotesCard :id="store.selected.id" :key="store.selected.id" :noteable="store.selected.noteable_type" type="support_reply" :readonly="!store.selected.can_reply || store.isSaving" :allow-attachments="false" :note-presentation="replyPresentation" @changed="store.refreshRequest()" />
    </template>
  </div>
</template>

<script setup>
import { watch } from 'vue'
import { useRoute } from 'vue-router'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import NotesCard from '../../../../../umisis/notes/resources/js/components/notes/NotesCard.vue'
import { useSupportRequestStore } from '../stores/supportRequestStore.js'
import { replyPresentation } from '../supportReplyPresentation.js'

const route = useRoute()
const store = useSupportRequestStore()
const statusLabel = value => ({ new: 'New', in_progress: 'In progress', closed: 'Closed' })[value] ?? value
const formatDate = value => new Date(value).toLocaleString()
const changeStatus = async event => {
  await store.updateStatus(event.target.value)
  event.target.value = store.selected?.status ?? event.target.value
}

watch(() => route.params.supportRequestId, id => store.fetchRequest(id), { immediate: true })
</script>
